/**
 * useRoadSegments Hook
 *
 * @module roadways/hooks
 * @description Hook para gestionar segmentos viales
 */

import { useState, useEffect, useCallback } from 'react';
import type { RoadSegment } from '../types/road-segment.types';

interface UseRoadSegmentsParams {
  projectId: string;
  autoLoad?: boolean;
}

interface UseRoadSegmentsReturn {
  roadSegments: RoadSegment[];
  isLoading: boolean;
  error: string | null;
  loadRoadSegments: () => Promise<void>;
  deleteRoadSegment: (id: string) => Promise<void>;
}

/**
 * Hook para obtener y gestionar segmentos viales
 *
 * @remarks
 * Por ahora retorna datos mock. Cuando el backend esté listo,
 * reemplazar con llamadas a la API.
 */
export function useRoadSegments({
  projectId,
  autoLoad = false,
}: UseRoadSegmentsParams): UseRoadSegmentsReturn {
  const [roadSegments, setRoadSegments] = useState<RoadSegment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRoadSegments = useCallback(async (): Promise<void> => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      // TODO: Reemplazar con llamada real a API
      // const response = await roadwaysApi.getRoadSegments(projectId);
      // setRoadSegments(response);

      // Mock data por ahora
      await new Promise(resolve => setTimeout(resolve, 500));
      setRoadSegments([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar segmentos viales');
      // eslint-disable-next-line no-console
      console.error('[useRoadSegments] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // eslint-disable-next-line @typescript-eslint/require-await
  const deleteRoadSegment = useCallback(async (id: string): Promise<void> => {
    try {
      // TODO: Reemplazar con llamada real a API
      // await roadwaysApi.deleteRoadSegment(id);

      setRoadSegments(prev => prev.filter(seg => seg.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar segmento');
      // eslint-disable-next-line no-console
      console.error('[useRoadSegments] Error deleting:', err);
      throw err;
    }
  }, []);

  useEffect(() => {
    if (autoLoad && projectId) {
      void loadRoadSegments();
    }
  }, [projectId, autoLoad, loadRoadSegments]);

  return {
    roadSegments,
    isLoading,
    error,
    loadRoadSegments,
    deleteRoadSegment,
  };
}
